import React, { useState, useEffect } from "react";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Button, TextField, Typography, Tooltip } from "@mui/material";

import api from "../../API/axios";
// @ts-ignore - JS hook without TS declarations
import { useSocialAuth } from "../../hooks/useSocialAuth";

// import NotificationSnackbar from "../../components/NotificationSnackbar";

// Helper function to calculate age from date of birth
const calculateAge = (dobString: string): number => {
  const dob = new Date(dobString);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

const Register = ({
  config,
  onAuthSuccess,
  onStateChange,
  setConsumerToken,
  showSnackbar,
  setPendingOAuthData,
}: {
  config: any;
  onAuthSuccess: (data: any) => void;
  onStateChange: (state: any) => void;
  setConsumerToken?: (token: string) => void;
  showSnackbar?: (message: string, severity: "success" | "error" | "warning" | "info") => void;
  setPendingOAuthData?: (data: any) => void;
}) => {
  
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showDobInfo, setShowDobInfo] = useState<boolean>(false);
  const [minimumAge, setMinimumAge] = useState<number>(18); // Default to 18

  // Use shared social auth hook with DOB collection flow
  const {
    socialLoading,
    error: socialError,
    setError: setSocialError,
    handleGoogleLogin: handleGoogleLoginBase,
    handleAppleLogin: handleAppleLoginBase,
    handleFacebookLogin: handleFacebookLoginBase,
  } = useSocialAuth({
    clientId: config?.clientId,
    onAuthSuccess,
    setConsumerToken,
    showSnackbar,
    onRequiresDob: setPendingOAuthData, // Store pending OAuth data when DOB is required
    onStateChange, // Navigate to collectDob state
  });

  // Social login handlers - trigger OAuth immediately, DOB collected after OAuth completes
  const handleGoogleLogin = () => {
    setError("");
    handleGoogleLoginBase({ mode: "register" }); // No DOB - will be collected after OAuth
  };

  const handleAppleLogin = () => {
    setError("");
    handleAppleLoginBase({ mode: "register" }); // No DOB - will be collected after OAuth
  };

  const handleFacebookLogin = () => {
    setError("");
    handleFacebookLoginBase({ mode: "register" }); // No DOB - will be collected after OAuth
  };

  // Fetch minimum age configuration from backend
  useEffect(() => {
    const fetchMinimumAge = async () => {
      try {
        const response = await api.get("/config/analytics");
        const configuredAge = response.data?.minimumAge;
        if (configuredAge && typeof configuredAge === "number" && configuredAge > 0) {
          setMinimumAge(configuredAge);
        }
      } catch (err) {
        console.error("Failed to fetch age config, using default 18:", err);
        // Keep default of 18
      }
    };
    fetchMinimumAge();
  }, []);
  // const [notification, setNotification] = useState({
  //   open: false,
  //   message: "",
  //   severity: "error" as "error" | "success" | "info" | "warning",
  // });

  // const showNotification = (message: string, severity: "error" | "success" | "info" | "warning" = "error") => {
  //   setNotification({
  //     open: true,
  //     message,
  //     severity,
  //   });
  // };

  // const handleCloseNotification = () => {
  //   setNotification((prev) => ({ ...prev, open: false }));
  // };





  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate date of birth - age restriction
    if (dob) {
      const age = calculateAge(dob);
      if (age < minimumAge) {
        const errorMsg = "Age verification failed";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      console.log("Checking email exists:", { email, clientId: config?.clientId });
      
      // Only check if email already exists - no consumer creation yet
      const response = await api.post("/consumer/check-email-exists", {
        email,
        clientId: Number(config?.clientId) || 0,
      });

      if (response.data?.exists) {
        // Email already registered - tell user to login
        const errorMsg = "This email is already registered. Please login instead.";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        return;
      }

      // Email is available - store registration data in localStorage (NO database entry yet)
      const pendingRegData = {
        firstName,
        surname,
        email,
        telephone,
        dob,
        clientId: config?.clientId,
        step: 'setPassword'
      };
      localStorage.setItem(`pendingRegistration_${config?.clientId}`, JSON.stringify(pendingRegData));

      console.log("Basic info stored, proceeding to password setup");

      // Pass basic data to auth context for display purposes
      onAuthSuccess({
        isAuthenticated: false,
        email,
        clientId: config?.clientId,
      });

      // Navigate to password setup
      onStateChange("setPassword");
    } catch (err: any) {
      console.error("Registration error:", err);
      let errorMsg = "Registration failed";
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      {/* Social Signup Buttons */}
      <div className="social-login-section">
        <div className="social-buttons">
            <button
              type="button"
              className="social-btn social-btn-apple"
              onClick={handleAppleLogin}
              disabled={loading || socialLoading !== ""}
            >
              <span className="social-icon social-icon-apple"></span>
              {socialLoading === "apple"
                ? "Connecting..."
                : "Sign up with Apple"}
            </button>
            <button
              type="button"
              className="social-btn social-btn-google"
              onClick={handleGoogleLogin}
              disabled={loading || socialLoading !== ""}
            >
              <span className="social-icon social-icon-google"></span>
              {socialLoading === "google"
                ? "Connecting..."
                : "Sign up with Google"}
            </button>
            {/* <button
              type="button"
              className="social-btn social-btn-facebook"
              onClick={() => setSocialLoading("facebook")}
              disabled={loading || socialLoading !== ""}
            >
              <span className="social-icon social-icon-facebook"></span>
              {socialLoading === "facebook"
                ? "Connecting..."
                : "Sign up with Facebook"}
            </button> */}
        </div>
        <span>OR</span>
      </div>
      <form onSubmit={handleSubmit}>
        {(error || socialError) && <div className="error-message">{error || socialError}</div>}

        {/* First Name and Surname in same row */}
        <div className="form-row">
          <TextField
            type="text"
            label="First Name"
            name="firstName"
            value={firstName}
            onChange={(e) =>
              setFirstName(e.target.value.replace(/[^A-Za-z ]/g, ""))
            }
            fullWidth
            required
            inputProps={{ maxLength: 50, pattern: "[A-Za-z ]*" }}
            sx={{
              width: "100%",
              marginBottom: 3,
            }}
            autoFocus
          />
          <TextField
            type="text"
            label="Surname"
            name="surname"
            value={surname}
            onChange={(e) =>
              setSurname(e.target.value.replace(/[^A-Za-z ]/g, ""))
            }
            fullWidth
            required
            inputProps={{ maxLength: 50, pattern: "[A-Za-z ]*" }}
            sx={{
              width: "100%",
              marginBottom: 3,
            }}
          />
        </div>

        {/* Email and Telephone in same row */}
        <div className="form-row">
          <TextField
            type="email"
            label="Email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 255 }}
            sx={{
              width: "100%",
              marginBottom: 3,
            }}
          />

          {/* <TextField
            type="number"
            label="Telephone"
            name="telephone"
            value={telephone}
            onChange={(e) =>
              setTelephone(e.target.value.replace(/[^0-9]/g, ""))
            }
            fullWidth
            required
            inputProps={{ maxLength: 15 }}
            sx={{
              width: "100%",
              marginBottom: 3,
            }}
          /> */}
          <TextField
            fullWidth
            label="Telephone"
            name="telephone"
            required
            value={telephone}
            onChange={(e: any) =>
              setTelephone(e.target.value.replace(/[^0-9]/g, ""))
            }
            sx={{
              width: "100%",
              marginBottom: 3,
            }}
            inputProps={{ maxLength: 15 }}
          />
        </div>

        {/* DOB and Continue button in same row */}
        <div className="form-row">
          <div style={{ width: "100%", marginBottom: 3 }}>
            <TextField
              type="date"
              label="Date of Birth"
              name="dob"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              fullWidth
              required
              disabled={loading}
              inputProps={{
                min: "1900-01-01",
                max: (() => {
                  const d = new Date();
                  return d.toISOString().split("T")[0];
                })(),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Tooltip
              title="Our services are age restricted and require age verification."
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    fontSize: "12px",
                    padding: "8px 12px",
                    "& .MuiTooltip-arrow": {
                      color: "#000",
                    },
                  },
                },
              }}
            >
              <span style={{ cursor: "pointer", color: "#666", fontSize: "12px", display: "inline-flex", alignItems: "center", marginTop: "4px" }}>
                <HelpOutlineIcon sx={{ fontSize: 14, marginRight: "4px" }} />
                Why do we need this?
              </span>
            </Tooltip>
          </div>

          <div style={{ width: "100%", marginBottom: 3 }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              sx={{
                width: "100%",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
              disabled={loading}
            >
              {loading ? "Checking..." : "Continue"}
            </Button>
          </div>
        </div>
        <div className="footer-links">
          <p>
            Already have an account?{" "}
            <Typography
              component="span"
              variant="body2"
              onClick={() => onStateChange("login")}
              color="var(--color-primary)"
              sx={{ cursor: "pointer" }}
            >
              Sign in
            </Typography>
          </p>
        </div>
      </form>

      {/* DOB Info Modal */}
      {showDobInfo && (
        <div className="modal-overlay" onClick={() => setShowDobInfo(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h4>Why do we need your date of birth?</h4>
            <p>
              We require your date of birth to comply with age restrictions and
              ensure a safe experience for all users. Your information is kept
              confidential and is only used for verification purposes.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowDobInfo(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      /> */}
    </div>
  );
};

export default Register;
